import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Field, TextInput, TextArea, Select, ImageInput, SaveBar } from "@/admin/ui";

const TABS = ["Hero", "Why Scalovix", "Why Choose Us", "Process", "About", "CTA Banner", "Service Pages"];
const SECTION_KEYS = { Hero: "hero", "Why Scalovix": "why_scalovix", "Why Choose Us": "why_choose_us", Process: "process", About: "about", "CTA Banner": "cta_banner", "Service Pages": "service_pages" };
const SERVICE_SLUGS = ["website-development", "ad-setup", "lead-generation", "ugc-videos"];

function ArrayEditor({ items, onChange, fields, addLabel, testid }) {
  const list = items || [];
  const set = (i, key, v) => {
    const next = list.map((it, idx) => (idx === i ? { ...it, [key]: v } : it));
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => {
    const blank = {};
    fields.forEach((f) => (blank[f.key] = f.type === "num" ? 0 : ""));
    onChange([...list, blank]);
  };

  return (
    <div className="space-y-4">
      {list.map((item, i) => (
        <div key={i} className="glass rounded-2xl p-5 relative" data-testid={`${testid}-item-${i}`}>
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono-label text-[9px] text-accent-bright">#{i + 1}</span>
            <div className="flex gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-subtle hover:text-white disabled:opacity-25" aria-label="Move up"><ChevronUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => move(i, 1)} disabled={i === list.length - 1} className="p-1.5 text-subtle hover:text-white disabled:opacity-25" aria-label="Move down"><ChevronDown className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(i)} className="p-1.5 text-subtle hover:text-rose-400" data-testid={`${testid}-remove-${i}`} aria-label="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-4">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                {f.type === "image" ? (
                  <Field label={f.label}><ImageInput value={item[f.key]} onChange={(v) => set(i, f.key, v)} testid={`${testid}-${i}-${f.key}`} /></Field>
                ) : f.type === "textarea" ? (
                  <Field label={f.label} hint={f.hint}><TextArea value={item[f.key]} onChange={(e) => set(i, f.key, e.target.value)} rows={f.rows || 2} testid={`${testid}-${i}-${f.key}`} /></Field>
                ) : (
                  <Field label={f.label} hint={f.hint}><TextInput value={item[f.key]} onChange={(e) => set(i, f.key, e.target.value)} testid={`${testid}-${i}-${f.key}`} /></Field>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button onClick={add} className="w-full rounded-2xl border border-dashed border-white/15 text-mist hover:text-white hover:border-accent/50 py-4 text-xs font-semibold flex items-center justify-center gap-2 transition-colors" data-testid={`${testid}-add-btn`}>
        <Plus className="h-4 w-4" /> {addLabel || "Add Item"}
      </button>
    </div>
  );
}

const pairToText = (arr) => (arr || []).map((x) => `${x.title || x.label || ""} :: ${x.body || x.value || ""}`).join("\n");
const textToPairs = (txt, aKey, bKey) =>
  String(txt || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const idx = l.indexOf("::");
    if (idx === -1) return { [aKey]: l, [bKey]: "" };
    return { [aKey]: l.slice(0, idx).trim(), [bKey]: l.slice(idx + 2).trim() };
  });

export default function ContentEditor() {
  const { settings, reload } = useSite();
  const [tab, setTab] = useState("Hero");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [svcSlug, setSvcSlug] = useState("website-development");

  useEffect(() => {
    if (settings && !draft) setDraft(JSON.parse(JSON.stringify(settings)));
  }, [settings]);

  if (!draft) {
    return <div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 text-accent-bright animate-spin" /></div>;
  }

  const sectionKey = SECTION_KEYS[tab];
  const section = draft[sectionKey] || {};

  const setSection = (patch) => setDraft((d) => ({ ...d, [sectionKey]: { ...d[sectionKey], ...patch } }));
  const svc = draft.service_pages?.[svcSlug] || {};
  const setSvc = (patch) => setDraft((d) => ({
    ...d,
    service_pages: { ...d.service_pages, [svcSlug]: { ...(d.service_pages?.[svcSlug] || {}), ...patch } },
  }));
  const setDeep = (path, value) => {
    setDraft((d) => {
      const next = { ...d };
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]] = { ...cur[keys[i]] };
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = sectionKey === "service_pages" ? { service_pages: draft.service_pages } : { [sectionKey]: draft[sectionKey] };
      await api.put("/admin/settings", payload);
      await reload();
      toast.success("Content updated — live on site");
    } catch (e) {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-content-editor">
      <h1 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight mb-2">Content Editor</h1>
      <p className="text-xs text-subtle mb-6">Edit any section of the public site. Changes go live the moment you save.</p>

      <div className="flex gap-2 flex-wrap mb-8">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all duration-300 ${tab === t ? "bg-accent border-accent text-white" : "border-white/15 text-mist hover:text-white hover:border-accent/50"}`}
            data-testid={`content-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Hero" && (
        <div className="glass rounded-2xl p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Media Mode" hint="Image, uploaded video or YouTube background">
              <Select value={section.mode || "image"} onChange={(e) => setSection({ mode: e.target.value })} options={[
                { value: "image", label: "Image" }, { value: "video", label: "Uploaded Video" }, { value: "youtube", label: "YouTube" },
              ]} testid="hero-mode-select" />
            </Field>
            <Field label="Overlay Opacity (0–1)" hint="0.6 = 60% dark overlay">
              <TextInput type="number" value={section.overlay} onChange={(e) => setSection({ overlay: Number(e.target.value) })} testid="hero-overlay-input" />
            </Field>
            <div className="sm:col-span-2"><Field label="Background Image"><ImageInput value={section.image_url} onChange={(v) => setSection({ image_url: v })} testid="hero-image" /></Field></div>
            <div className="sm:col-span-2"><Field label="Uploaded Video URL (video mode)"><TextInput value={section.video_url} onChange={(e) => setSection({ video_url: e.target.value })} testid="hero-video-url" /></Field></div>
            <div className="sm:col-span-2"><Field label="YouTube URL (youtube mode)"><TextInput value={section.youtube_url} onChange={(e) => setSection({ youtube_url: e.target.value })} testid="hero-youtube-url" /></Field></div>
            <Field label="Badge Text"><TextInput value={section.badge} onChange={(e) => setSection({ badge: e.target.value })} testid="hero-badge-input" /></Field>
            <Field label="Highlight Word" hint="Word from line 2 rendered in gradient"><TextInput value={section.highlight_word} onChange={(e) => setSection({ highlight_word: e.target.value })} testid="hero-highlight-input" /></Field>
            <Field label="Headline — Line 1"><TextInput value={section.title_line_1} onChange={(e) => setSection({ title_line_1: e.target.value })} testid="hero-title-line1" /></Field>
            <Field label="Headline — Line 2"><TextInput value={section.title_line_2} onChange={(e) => setSection({ title_line_2: e.target.value })} testid="hero-title-line2" /></Field>
            <div className="sm:col-span-2"><Field label="Subheading"><TextArea value={section.subtitle} onChange={(e) => setSection({ subtitle: e.target.value })} rows={3} testid="hero-subtitle-input" /></Field></div>
            <Field label="Primary CTA — Label"><TextInput value={section.cta_primary?.label} onChange={(e) => setDeep("hero.cta_primary.label", e.target.value)} testid="hero-cta1-label" /></Field>
            <Field label="Primary CTA — Link" hint="Use whatsapp:general for WhatsApp"><TextInput value={section.cta_primary?.href} onChange={(e) => setDeep("hero.cta_primary.href", e.target.value)} testid="hero-cta1-href" /></Field>
            <Field label="Secondary CTA — Label"><TextInput value={section.cta_secondary?.label} onChange={(e) => setDeep("hero.cta_secondary.label", e.target.value)} testid="hero-cta2-label" /></Field>
            <Field label="Secondary CTA — Link"><TextInput value={section.cta_secondary?.href} onChange={(e) => setDeep("hero.cta_secondary.href", e.target.value)} testid="hero-cta2-href" /></Field>
          </div>
          <SaveBar onSave={save} saving={saving} testid="content-save-hero-btn" />
        </div>
      )}

      {tab === "Why Scalovix" && (
        <div className="glass rounded-2xl p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Section Heading"><TextInput value={section.heading} onChange={(e) => setSection({ heading: e.target.value })} testid="why-heading-input" /></Field>
            <Field label="Section Sub"><TextInput value={section.sub} onChange={(e) => setSection({ sub: e.target.value })} testid="why-sub-input" /></Field>
          </div>
          <p className="font-mono-label text-[10px] text-subtle mb-4">Manifesto Chapters</p>
          <ArrayEditor
            items={section.chapters}
            onChange={(v) => setSection({ chapters: v })}
            fields={[
              { key: "num", label: "Number", hint: "01" },
              { key: "title", label: "Title" },
              { key: "body", label: "Body", type: "textarea", full: true },
              { key: "image_url", label: "Image (mobile view)", type: "image", full: true },
            ]}
            addLabel="Add Chapter"
            testid="chapters-editor"
          />
          <SaveBar onSave={save} saving={saving} testid="content-save-why-btn" />
        </div>
      )}

      {tab === "Why Choose Us" && (
        <div className="glass rounded-2xl p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Section Heading"><TextInput value={section.heading} onChange={(e) => setSection({ heading: e.target.value })} testid="wcu-heading-input" /></Field>
            <Field label="Section Sub"><TextInput value={section.sub} onChange={(e) => setSection({ sub: e.target.value })} testid="wcu-sub-input" /></Field>
          </div>
          <p className="font-mono-label text-[10px] text-subtle mb-4">Feature Cards</p>
          <ArrayEditor
            items={section.items}
            onChange={(v) => setSection({ items: v })}
            fields={[
              { key: "icon", label: "Icon (Lucide name)", hint: "Rocket, Target, Code2, BarChart3, MessageCircle, ShieldCheck..." },
              { key: "title", label: "Title" },
              { key: "body", label: "Body", type: "textarea", full: true },
            ]}
            addLabel="Add Feature"
            testid="wcu-editor"
          />
          <SaveBar onSave={save} saving={saving} testid="content-save-wcu-btn" />
        </div>
      )}

      {tab === "Process" && (
        <div className="glass rounded-2xl p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Section Heading"><TextInput value={section.heading} onChange={(e) => setSection({ heading: e.target.value })} testid="process-heading-input" /></Field>
            <Field label="Section Sub"><TextInput value={section.sub} onChange={(e) => setSection({ sub: e.target.value })} testid="process-sub-input" /></Field>
          </div>
          <p className="font-mono-label text-[10px] text-subtle mb-4">Timeline Steps</p>
          <ArrayEditor
            items={section.steps}
            onChange={(v) => setSection({ steps: v })}
            fields={[
              { key: "num", label: "Number" },
              { key: "title", label: "Title" },
              { key: "body", label: "Body", type: "textarea", full: true },
            ]}
            addLabel="Add Step"
            testid="process-editor"
          />
          <SaveBar onSave={save} saving={saving} testid="content-save-process-btn" />
        </div>
      )}

      {tab === "About" && (
        <div className="glass rounded-2xl p-6 sm:p-8">
          <Field label="Heading"><TextInput value={section.heading} onChange={(e) => setSection({ heading: e.target.value })} testid="about-heading-input" /></Field>
          <Field label="Story Paragraphs" hint="Separate paragraphs with a blank line">
            <TextArea value={(section.story || []).join("\n\n")} onChange={(e) => setSection({ story: e.target.value.split("\n\n").filter(Boolean) })} rows={8} testid="about-story-input" />
          </Field>
          <Field label="Studio Image"><ImageInput value={section.image_url} onChange={(v) => setSection({ image_url: v })} testid="about-image" /></Field>
          <p className="font-mono-label text-[10px] text-subtle mb-4 mt-6">Animated Stats</p>
          <ArrayEditor
            items={section.stats}
            onChange={(v) => setSection({ stats: v })}
            fields={[
              { key: "value", label: "Value", hint: "40" },
              { key: "suffix", label: "Suffix", hint: "+ / x / %" },
              { key: "label", label: "Label" },
            ]}
            addLabel="Add Stat"
            testid="about-stats-editor"
          />
          <p className="font-mono-label text-[10px] text-subtle mb-4 mt-6">Core Values</p>
          <ArrayEditor
            items={section.values}
            onChange={(v) => setSection({ values: v })}
            fields={[
              { key: "title", label: "Title" },
              { key: "body", label: "Body", type: "textarea", full: true },
            ]}
            addLabel="Add Value"
            testid="about-values-editor"
          />
          <SaveBar onSave={save} saving={saving} testid="content-save-about-btn" />
        </div>
      )}

      {tab === "CTA Banner" && (
        <div className="glass rounded-2xl p-6 sm:p-8">
          <Field label="Heading"><TextInput value={section.heading} onChange={(e) => setSection({ heading: e.target.value })} testid="cta-heading-input" /></Field>
          <Field label="Subtext"><TextArea value={section.sub} onChange={(e) => setSection({ sub: e.target.value })} rows={2} testid="cta-sub-input" /></Field>
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Button Label"><TextInput value={section.button_label} onChange={(e) => setSection({ button_label: e.target.value })} testid="cta-button-label" /></Field>
            <Field label="Button Link" hint="whatsapp:general → WhatsApp with general template, or a path like /contact"><TextInput value={section.button_href} onChange={(e) => setSection({ button_href: e.target.value })} testid="cta-button-href" /></Field>
          </div>
          <SaveBar onSave={save} saving={saving} testid="content-save-cta-btn" />
        </div>
      )}

      {tab === "Service Pages" && (
        <div>
          <div className="flex gap-2 flex-wrap mb-6">
            {SERVICE_SLUGS.map((s) => (
              <button
                key={s}
                onClick={() => setSvcSlug(s)}
                className={`px-4 py-2 rounded-full text-[11px] font-semibold border transition-all duration-300 ${svcSlug === s ? "bg-accent border-accent text-white" : "border-white/15 text-mist hover:text-white hover:border-accent/50"}`}
                data-testid={`service-page-tab-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="glass rounded-2xl p-6 sm:p-8">
            <div className="grid sm:grid-cols-2 gap-x-4">
              <Field label="Heading"><TextInput value={svc.heading} onChange={(e) => setSvc({ heading: e.target.value })} testid="svc-heading-input" /></Field>
              <Field label="Offer Badge"><TextInput value={svc.badge} onChange={(e) => setSvc({ badge: e.target.value })} testid="svc-badge-input" /></Field>
              <Field label="Price"><TextInput value={svc.price} onChange={(e) => setSvc({ price: e.target.value })} testid="svc-price-input" /></Field>
              <Field label="Strike Price"><TextInput value={svc.strike_price} onChange={(e) => setSvc({ strike_price: e.target.value })} testid="svc-strike-input" /></Field>
            </div>
            <Field label="Subheading"><TextArea value={svc.sub} onChange={(e) => setSvc({ sub: e.target.value })} rows={2} testid="svc-sub-input" /></Field>

            {svcSlug === "website-development" && (
              <>
                <p className="font-mono-label text-[10px] text-subtle mb-3 mt-2">Website Gate Question</p>
                <div className="grid sm:grid-cols-2 gap-x-4">
                  <Field label="Question"><TextInput value={svc.gate?.question} onChange={(e) => setDeep(`service_pages.${svcSlug}.gate.question`, e.target.value)} testid="svc-gate-question" /></Field>
                  <Field label="Yes → CTA Label"><TextInput value={svc.gate?.yes_cta_label} onChange={(e) => setDeep(`service_pages.${svcSlug}.gate.yes_cta_label`, e.target.value)} testid="svc-gate-cta" /></Field>
                  <Field label="Yes → Note"><TextArea value={svc.gate?.yes_note} onChange={(e) => setDeep(`service_pages.${svcSlug}.gate.yes_note`, e.target.value)} rows={2} testid="svc-gate-yes" /></Field>
                  <Field label="No → Note"><TextArea value={svc.gate?.no_note} onChange={(e) => setDeep(`service_pages.${svcSlug}.gate.no_note`, e.target.value)} rows={2} testid="svc-gate-no" /></Field>
                </div>
                <Field label="Included Features (one per line)" hint="8 items recommended">
                  <TextArea value={(svc.features || []).join("\n")} onChange={(e) => setSvc({ features: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} rows={8} mono testid="svc-features-input" />
                </Field>
                <p className="font-mono-label text-[10px] text-subtle mb-3 mt-2">5-Step Payment Process</p>
                <Field label="Steps (one per line as 'Title :: description')">
                  <TextArea value={pairToText(svc.payment_steps)} onChange={(e) => setSvc({ payment_steps: textToPairs(e.target.value, "title", "body") })} rows={10} mono testid="svc-payment-input" />
                </Field>
              </>
            )}

            {(svcSlug === "ad-setup" || svcSlug === "lead-generation" || svcSlug === "ugc-videos") && (
              <Field label="Process Timeline (one per line as 'Title :: description')">
                <TextArea value={pairToText(svc.process)} onChange={(e) => setSvc({ process: textToPairs(e.target.value, "title", "body") })} rows={10} mono testid="svc-process-input" />
              </Field>
            )}

            {svcSlug === "ad-setup" && (
              <Field label="Pricing Breakdown (one per line as 'Label :: value')">
                <TextArea value={pairToText(svc.pricing_rows)} onChange={(e) => setSvc({ pricing_rows: textToPairs(e.target.value, "label", "value") })} rows={6} mono testid="svc-pricing-rows-input" />
              </Field>
            )}

            {svcSlug === "lead-generation" && (
              <Field label="Industry Tags (comma separated)">
                <TextArea value={(svc.industries || []).join(", ")} onChange={(e) => setSvc({ industries: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} rows={2} testid="svc-industries-input" />
              </Field>
            )}

            <Field label="Pricing Notes"><TextArea value={svc.pricing_notes} onChange={(e) => setSvc({ pricing_notes: e.target.value })} rows={3} testid="svc-pricing-notes-input" /></Field>
            <SaveBar onSave={save} saving={saving} testid="content-save-service-btn" />
          </div>
        </div>
      )}
    </div>
  );
}
